import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Alert,
  AlertDescription,
  Skeleton,
} from "@tellescope/ui";
import { formatCardBrand } from "@tellescope/utils";
import { CreditCard } from "lucide-react";
import type { StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import { useStripeSetupIntent } from "../../../hooks/useStripeSetupIntent";
import { useStripeSubscriptionMutation } from "../../../hooks/useStripeSubscriptionMutations";

interface UpdatePaymentMethodDialogProps {
  subscription: StripeSubscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface PaymentMethodFormProps {
  subscriptionId: string;
  onSuccess?: () => void;
  onCancel: () => void;
}

/**
 * Inner form component that must be rendered inside Elements provider.
 * Handles the actual payment method collection and submission.
 */
function PaymentMethodForm({ subscriptionId, onSuccess, onCancel }: PaymentMethodFormProps) {
  const { t } = useTranslation("payments");
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updatePaymentMethod = useStripeSubscriptionMutation();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    // Confirm the SetupIntent to save the payment method
    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message ?? t("An error occurred while saving your payment method."));
      setIsSubmitting(false);
      return;
    }

    if (!setupIntent?.payment_method) {
      setError(t("Failed to save payment method. Please try again."));
      setIsSubmitting(false);
      return;
    }

    // Update the subscription with the new payment method
    updatePaymentMethod.mutate(
      {
        integration: "Stripe",
        type: "subscription-update-payment-method",
        id: subscriptionId,
        query: { paymentMethodId: setupIntent.payment_method as string },
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          onSuccess?.();
        },
        onError: (err: Error) => {
          console.error("Payment method update error:", err);
          setError(t("Failed to update payment method. Please try again."));
          setIsSubmitting(false);
        },
      }
    );
  };

  const isDisabled = !stripe || !elements || !isReady || isSubmitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        onReady={() => setIsReady(true)}
        options={{
          layout: "tabs",
        }}
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t("Cancel")}
        </Button>
        <Button type="submit" disabled={isDisabled} loading={isSubmitting}>
          {t("Update Payment Method")}
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * Loading skeleton for the payment form
 */
function PaymentFormSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-40 w-full" />
      <div className="flex justify-end gap-2">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-36" />
      </div>
    </div>
  );
}

export function UpdatePaymentMethodDialog({
  subscription,
  open,
  onOpenChange,
  onSuccess,
}: UpdatePaymentMethodDialogProps) {
  const { t } = useTranslation("payments");

  // Fetch SetupIntent when dialog opens (refetchOnMount: 'always' ensures fresh data)
  const {
    data: setupIntent,
    isLoading: isSetupIntentLoading,
    error: setupIntentError,
  } = useStripeSetupIntent({ enabled: open });

  // Load Stripe when we have the publishable key
  const { data: stripeInstance, isLoading: isStripeLoading } = useQuery({
    queryKey: ["stripe", "loadStripe", setupIntent?.publishableKey],
    queryFn: () => loadStripe(setupIntent?.publishableKey ?? ""),
    enabled: !!setupIntent?.publishableKey,
    staleTime: Infinity, // Stripe instance never goes stale
  });

  // Derived state - compute stripePromise directly from stripeInstance
  const stripePromise = stripeInstance ? Promise.resolve(stripeInstance) : null;

  const cardInfo = subscription.default_payment_method?.card;
  const isLoading = isSetupIntentLoading || isStripeLoading || !stripeInstance || !setupIntent?.clientSecret;

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleSuccess = () => {
    onSuccess?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("Update Payment Method")}</DialogTitle>
        </DialogHeader>

        {/* Current payment method */}
        {cardInfo && (
          <div className="p-3 bg-muted rounded-md mb-4">
            <p className="text-sm text-muted-foreground mb-1">{t("Current payment method")}</p>
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="font-medium">
                {formatCardBrand(cardInfo.brand)} ****{cardInfo.last4}
              </span>
              <span className="text-muted-foreground text-sm">
                {t("Expires")} {cardInfo.exp_month}/{cardInfo.exp_year}
              </span>
            </div>
          </div>
        )}

        {/* Error state */}
        {setupIntentError && (
          <Alert variant="destructive">
            <AlertDescription>{t("Failed to initialize payment form. Please try again.")}</AlertDescription>
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && !setupIntentError && <PaymentFormSkeleton />}

        {/* Payment form */}
        {!isLoading && stripePromise && setupIntent?.clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: setupIntent.clientSecret,
              appearance: {
                theme: "stripe",
                variables: {
                  colorPrimary: "#0f172a",
                },
              },
            }}
          >
            <PaymentMethodForm subscriptionId={subscription.id} onSuccess={handleSuccess} onCancel={handleCancel} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}
